export function formatMissingSettings(missingSettings) {
  if (!Array.isArray(missingSettings) || !missingSettings.length) {
    return "";
  }

  return `Missing settings: ${missingSettings.join(", ")}`;
}

export function flattenError(payload) {
  if (!payload) {
    return "";
  }
  if (typeof payload === "string") {
    return payload;
  }
  if (Array.isArray(payload)) {
    return payload.map(flattenError).filter(Boolean).join(" ");
  }

  const messages = [];

  if (payload.detail) {
    messages.push(flattenError(payload.detail));
  }

  const missingSettingsMessage = formatMissingSettings(payload.missing_settings);
  if (missingSettingsMessage) {
    messages.push(missingSettingsMessage);
  }

  return [
    ...messages,
    ...Object.entries(payload)
      .filter(([key]) => key !== "detail" && key !== "missing_settings")
      .map(([, value]) => flattenError(value))
      .filter(Boolean),
  ]
    .filter(Boolean)
    .join(" ");
}

export function normalizeText(value) {
  return String(value || "").trim().toLowerCase();
}

function extractAccessLabels(value) {
  if (!value) {
    return [];
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => extractAccessLabels(item));
  }

  if (typeof value === "object") {
    const namedValue = ["label", "name", "key", "code", "title", "id"]
      .map((key) => value[key])
      .find(Boolean);
    if (namedValue) {
      return extractAccessLabels(namedValue);
    }

    return Object.entries(value)
      .filter(([, enabled]) => Boolean(enabled))
      .flatMap(([key]) => extractAccessLabels(key));
  }

  return [];
}

export function buildAccessLookup(value) {
  return new Set(extractAccessLabels(value).map((item) => normalizeText(item).replace(/[_-]+/g, " ")));
}

export function hasNamedAccess(accessLookup, ...candidates) {
  return candidates
    .flat()
    .some((candidate) => accessLookup.has(normalizeText(candidate).replace(/[_-]+/g, " ")));
}

export function userHasNamedTabAccess(user, ...candidates) {
  return hasNamedAccess(buildAccessLookup(user?.tab_access), ...candidates);
}

export function filterItemsByAccess(items = [], accessLookup) {
  if (!(accessLookup instanceof Set) || accessLookup.size === 0) {
    return items;
  }

  return items.filter((item) => hasNamedAccess(accessLookup, item));
}
export function buildPagination(totalPages, currentPage) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const left = Math.max(2, currentPage - 2);
  const right = Math.min(totalPages - 1, currentPage + 2);

  const items = [1];
  if (left > 2) items.push("ellipsis");
  for (let i = left; i <= right; i++) items.push(i);
  if (right < totalPages - 1) items.push("ellipsis");
  items.push(totalPages);
  return items;
}

export function toneForSource(sourceName, sourceType) {
  if (sourceType === "ads") {
    return sourceName?.toLowerCase().includes("google") ? "violet" : "orange";
  }
  if (sourceType === "walk_in") {
    return "amber";
  }
  if (sourceType === "referral") {
    return "blue";
  }
  if (sourceType === "ivr") {
    return "mint";
  }
  if (sourceType === "website") {
    return "orange";
  }
  return "blue";
}

export function toneForDisposition(label) {
  const value = (label || "").toLowerCase();
  if (value.includes("contact")) return "green";
  if (value.includes("book")) return "violet";
  if (value.includes("callback")) return "sky";
  if (value.includes("invalid") || value.includes("interested")) return "red";
  if (value.includes("consultation") || value.includes("language")) return "gold";
  if (value.includes("appoint")) return "violet";
  return "orange";
}

export function toneForAgent(label) {
  return label === "Top" ? "green" : "mint";
}

export function riskTone(label) {
  if (label === "High") return "high";
  if (label === "Medium") return "medium";
  return "low";
}

export function formatCompactNumber(value) {
  return new Intl.NumberFormat("en-IN").format(Number(value || 0));
}

export function formatRate(value) {
  return Number(value || 0).toFixed(Number(value || 0) % 1 === 0 ? 0 : 1);
}

export function formatMonthLabel(value) {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(value);
}

export function initialsForName(value) {
  return (value || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

export function formatPhone(value) {
  if (!value) {
    return "—";
  }
  return value.replace(/\s+/g, " ");
}

export function displayPhone(lead, roleScope) {
  if (!lead) return "—";
  const full = lead.phone_number || lead.lead_phone_number || "";
  const masked = lead.masked_phone || lead.lead_masked_phone || "";
  if (!full) return "—";
  if (roleScope?.can_see_full_phone) return full;
  return masked || `${"*".repeat(Math.max(0, full.length - 4))}${full.slice(-4)}`;
}

export function formatDateCell(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  const datePart = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    timeZone: "Asia/Kolkata",
  }).format(date);
  const timePart = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  }).format(date);

  return `${datePart} ${timePart}`;
}

function formatDuration(ms) {
  const abs = Math.abs(ms);
  const minutes = Math.floor(abs / 60000);
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rem = minutes % 60;
    return rem ? `${hours}h ${rem}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const remH = hours % 24;
  return remH ? `${days}d ${remH}h` : `${days}d`;
}

export function formatTimeSince(value) {
  if (!value) return "—";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Date.now() - then;
  if (diff < 0) return "—";
  return formatDuration(diff);
}

export function formatTimeLeft(value) {
  if (!value) return "—";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = then - Date.now();
  if (diff < 0) return `Overdue ${formatDuration(diff)}`;
  return formatDuration(diff);
}

export function timeLeftStatus(value) {
  if (!value) return { tone: "muted", overdue: false, severeOverdue: false, hoursOverdue: 0 };
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return { tone: "muted", overdue: false, severeOverdue: false, hoursOverdue: 0 };
  const diffMs = then - Date.now();
  if (diffMs >= 0) return { tone: "ok", overdue: false, severeOverdue: false, hoursOverdue: 0 };
  const hoursOverdue = Math.abs(diffMs) / (1000 * 60 * 60);
  return {
    tone: hoursOverdue >= 24 ? "severe" : "warn",
    overdue: true,
    severeOverdue: hoursOverdue >= 24,
    hoursOverdue,
  };
}

export function toInputDateTime(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 16);
}

export function startCase(value) {
  return (value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function labelForActivity(activityType) {
  return startCase(activityType || "activity");
}

export function getRootDispositions(groups) {
  return groups
    .map((group) => group.dispositions?.[0])
    .filter(Boolean);
}

const SOURCE_DISPLAY_ORDER = [
  'facebook', 'instagram', 'referrals', 'walk-in', 'website',
  'in bound', 'email', 'offline events', 'linkedin', 'others',
];

export function buildOrderedLeadSourceOptions(leadSources = []) {
  return [...leadSources]
    .sort((left, right) => {
      const li = SOURCE_DISPLAY_ORDER.indexOf(left.name.toLowerCase().trim());
      const ri = SOURCE_DISPLAY_ORDER.indexOf(right.name.toLowerCase().trim());
      const lo = li === -1 ? SOURCE_DISPLAY_ORDER.length : li;
      const ro = ri === -1 ? SOURCE_DISPLAY_ORDER.length : ri;
      return lo - ro;
    })
    .map((source) => ({ value: String(source.id), label: source.name }));
}

export function flattenDispositionOptions(groups) {
  return groups.flatMap((group) => {
    const root = group.dispositions?.[0];
    if (!root) {
      return [];
    }

    return [
      { id: root.id, label: group.name, kind: "group" },
      ...root.children.map((child) => ({
        id: child.id,
        label: `${group.name} / ${child.name}`,
        kind: "child",
      })),
    ];
  });
}

export function downloadCsv(filename, header, rows) {
  const csv = [header, ...rows]
    .map((row) =>
      row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","),
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export async function downloadViaBrowser(url) {
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) {
    let detail = "";
    try { detail = (await response.json()).detail || ""; } catch (_) {}
    throw new Error(detail || `Download failed (${response.status})`);
  }

  let filename = "download";
  const disposition = response.headers.get("Content-Disposition");
  if (disposition) {
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    if (match?.[1]) filename = match[1].trim();
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

export function buildLeadRequestPayload({ currentUser, leadForm, mode, roleScope }) {
  const assignedToId =
    roleScope?.scope === "own_leads" ? currentUser?.id : (leadForm.assignedToId || null);

  if (mode === "edit" && roleScope?.lead_edit_scope === "notes_assignment_only") {
    return {
      assigned_to: assignedToId || null,
      notes: leadForm.notes,
    };
  }

  return {
    full_name: leadForm.fullName.trim(),
    phone_number: leadForm.phoneNumber.trim(),
    whatsapp_number: leadForm.whatsappSameAsMobile
      ? leadForm.phoneNumber.trim()
      : leadForm.whatsappNumber.trim(),
    email: leadForm.email.trim(),
    sub_source: leadForm.subSource || "",
    profession: leadForm.profession || "",
    source: leadForm.sourceId || null,
    assigned_to: assignedToId || null,
    region_name: leadForm.regionName.trim(),
    city_name: leadForm.cityName.trim(),
    pincode: leadForm.pincode?.trim() || "",
    notes: leadForm.notes,
    ...(leadForm.dispositionId ? { current_disposition: leadForm.dispositionId } : {}),
    ...(leadForm.subDispositionId ? { current_sub_disposition: leadForm.subDispositionId } : {}),
    next_follow_up_at: leadForm.nextFollowUp
      ? new Date(leadForm.nextFollowUp).toISOString()
      : null,
  };
}

export function buildGeographyData(geographies = [], availableRegions = [], availableCities = []) {
  const regionNameById = new Map();
  const cityOptionsByRegion = new Map();
  const cityNodeByKey = new Map();
  const regionNodeByName = new Map();

  geographies.forEach((node) => {
    if (normalizeText(node.node_type) === "region") {
      regionNameById.set(node.id, node.name);
      regionNodeByName.set(normalizeText(node.name), node);
      if (!cityOptionsByRegion.has(node.name)) {
        cityOptionsByRegion.set(node.name, []);
      }
    }
  });

  geographies.forEach((node) => {
    if (normalizeText(node.node_type) !== "city") {
      return;
    }
    const regionName = regionNameById.get(node.parent);
    const key = `${normalizeText(regionName)}::${normalizeText(node.name)}`;
    cityNodeByKey.set(key, node);
    if (!regionName) {
      return;
    }
    const current = cityOptionsByRegion.get(regionName) || [];
    current.push(node.name);
    cityOptionsByRegion.set(regionName, current);
  });

  const regionOptions = Array.from(
    new Set([
      ...Array.from(regionNodeByName.values()).map((node) => node.name),
      ...availableRegions.filter(Boolean),
    ]),
  );

  const normalizedCityOptionsByRegion = Array.from(cityOptionsByRegion.entries()).reduce(
    (accumulator, [regionName, cityNames]) => ({
      ...accumulator,
      [regionName]: Array.from(new Set(cityNames.filter(Boolean))).sort((left, right) =>
        left.localeCompare(right),
      ),
    }),
    {},
  );

  const knownCitiesSet = new Set(
    Object.values(normalizedCityOptionsByRegion)
      .flat()
      .map((c) => normalizeText(c)),
  );

  const otherCities = Array.from(
    new Set(availableCities.filter(Boolean).filter((c) => !knownCitiesSet.has(normalizeText(c)))),
  ).sort((left, right) => left.localeCompare(right));

  const finalCityOptionsByRegion = { ...normalizedCityOptionsByRegion };
  const finalRegionOptions = [...regionOptions];

  if (otherCities.length > 0) {
    finalCityOptionsByRegion["Other Cities"] = otherCities;
    finalRegionOptions.push("Other Cities");
  }

  const allCities = Array.from(
    new Set([
      ...Object.values(finalCityOptionsByRegion).flat(),
      ...availableCities.filter(Boolean),
    ]),
  ).sort((left, right) => left.localeCompare(right));

  const cityToRegion = {};
  Object.entries(finalCityOptionsByRegion).forEach(([regionName, cities]) => {
    cities.forEach((city) => {
      cityToRegion[normalizeText(city)] = regionName;
    });
  });

  return {
    allCities,
    cityNodeByKey,
    cityOptionsByRegion: finalCityOptionsByRegion,
    cityToRegion,
    otherCities,
    regionNodeByName,
    regionOptions: finalRegionOptions.sort((a, b) => {
      if (a === "Other Cities") return 1;
      if (b === "Other Cities") return -1;
      return a.localeCompare(b);
    }),
  };
}

export function getCityOptionsForRegion(cityOptionsByRegion, regionName, fallbackCities = []) {
  if (!regionName) {
    return fallbackCities;
  }
  const regionNames = String(regionName)
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);

  if (!regionNames.length) {
    return fallbackCities;
  }

  const mergedCities = Array.from(
    new Set(
      regionNames.flatMap((name) => cityOptionsByRegion[name] || []),
    ),
  ).sort((left, right) => left.localeCompare(right));

  return mergedCities.length ? mergedCities : fallbackCities;
}

export function resolveGeographyNode({ cityName, regionName, geographyData }) {
  const normalizedRegion = normalizeText(regionName);
  const normalizedCity = normalizeText(cityName);

  if (normalizedRegion && normalizedCity) {
    return (
      geographyData?.cityNodeByKey.get(`${normalizedRegion}::${normalizedCity}`) || null
    );
  }

  if (normalizedRegion) {
    return geographyData?.regionNodeByName.get(normalizedRegion) || null;
  }

  return null;
}

export function matchesGeography(match, reference) {
  return (
    normalizeText(match?.city_name) === normalizeText(reference?.city_name) &&
    normalizeText(match?.region_name) === normalizeText(reference?.region_name)
  );
}

export function filterDuplicateMatchesForRole(matches = [], referenceLead, roleScope) {
  if (roleScope?.can_view_cross_city_duplicates) {
    return matches;
  }

  return matches.filter((match) => matchesGeography(match, referenceLead));
}

export function getFileExtension(filename = "") {
  const parts = String(filename).split(".");
  return parts.length > 1 ? normalizeText(parts[parts.length - 1]) : "";
}

export function validateDocumentSelection({
  allowedExtensions = [],
  existingCount = 0,
  files = [],
  maxDocuments = 4,
}) {
  if (!files.length) {
    return "Choose at least one document to upload.";
  }

  const remainingSlots = Math.max(maxDocuments - existingCount, 0);
  if (!remainingSlots) {
    return `This lead already has the maximum of ${maxDocuments} documents.`;
  }

  if (files.length > remainingSlots) {
    return `You can upload only ${remainingSlots} more document${remainingSlots === 1 ? "" : "s"} for this lead.`;
  }

  const invalidFile = files.find(
    (file) => !allowedExtensions.includes(getFileExtension(file?.name || "")),
  );
  if (invalidFile) {
    return `Unsupported file type for ${invalidFile.name}.`;
  }

  return "";
}

export function getLeadJourneyHighlights(sourceHistory = []) {
  const highlightOrder = [
    "Lead added again",
    "Duplicate detected",
    "Geography added",
    "Source added",
  ];

  return highlightOrder
    .map((title) => sourceHistory.find((entry) => entry.title === title))
    .filter(Boolean);
}

